import { SystemSettings } from '@/shared/types';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { readSettingsCache, writeSettingsCache } from '@/shared/utils/settingsCache';
import { useAuth } from './AuthContext';
import { useDI } from './DIContext';

export interface SettingsContextType {
  systemSettings: SystemSettings | null;
  updateSystemSettings: (newSettings: Partial<SystemSettings>) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | null>(null);

const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
    : '14 165 233';
};

const applyTheme = (primaryColor?: string): void => {
  if (!primaryColor) return;
  const rgb = hexToRgb(primaryColor);
  const root = document.documentElement;
  root.style.setProperty('--color-primary-500', rgb);
  root.style.setProperty('--color-primary-600', rgb);
  root.style.setProperty('--color-primary-700', rgb);
};

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settingsRepo } = useDI();
  const { currentUser, isLoadingUser } = useAuth();
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);

  // Apply cached branding on first paint (before any network call)
  useEffect(() => {
    const cached = readSettingsCache();
    if (!cached) return;
    setSystemSettings(cached);
    applyTheme(cached.branding?.primaryColor);
  }, []);

  // Re-fetch settings whenever the authenticated user changes
  useEffect(() => {
    if (isLoadingUser || !currentUser) return;
    settingsRepo
      .getSettings()
      .then((settings) => {
        setSystemSettings(settings);
        writeSettingsCache(settings);
        applyTheme(settings.branding?.primaryColor);
      })
      .catch((e) => console.warn('Failed to load settings from server', e));
  }, [currentUser?.id, isLoadingUser, settingsRepo]);

  const updateSystemSettings = async (newSettings: Partial<SystemSettings>): Promise<void> => {
    if (!systemSettings) return;
    const updated = await settingsRepo.updateSettings(newSettings);
    setSystemSettings(updated);
    writeSettingsCache(updated);
    if (newSettings.branding?.primaryColor) applyTheme(newSettings.branding.primaryColor);
  };

  return (
    <SettingsContext.Provider value={{ systemSettings, updateSystemSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within a SettingsProvider');
  return ctx;
};
